import { RefreshIcon } from '@solar-icons/react/linear/refresh'
import { cn } from '@/lib/utils/cn'
import { useHealth } from '@/lib/hooks/useHealth'
import { useSettingsStore } from '@/stores/settingsStore'

const LED_CLASS: Record<string, string> = {
  online: 'bg-success shadow-[0_0_0_3px] shadow-success/15',
  offline: 'bg-danger shadow-[0_0_0_3px] shadow-danger/15',
  checking: 'bg-warning shadow-[0_0_0_3px] shadow-warning/15',
}

const LABEL: Record<string, string> = {
  online: 'Connected',
  offline: 'Offline',
  checking: 'Checking',
}

export interface ConnectionIndicatorProps {
  className?: string
  /** Icon-rail form: a plain dot in a circular hit target, no label. */
  compact?: boolean
}

/**
 * Live backend status. Clicking re-probes immediately rather than waiting for
 * the poll interval — the common case is "I just started uvicorn".
 */
export function ConnectionIndicator({ className, compact }: ConnectionIndicatorProps) {
  const { status, health, error, refetch, isFetching } = useHealth()
  const baseUrl = useSettingsStore((state) => state.baseUrl)

  const title =
    status === 'online'
      ? `${health?.application ?? 'QAP'} v${health?.version ?? '?'} · ${health?.environment ?? ''} · ${baseUrl}`
      : status === 'offline'
        ? (error?.message ?? `No response from ${baseUrl}`)
        : `Probing ${baseUrl}`

  if (compact) {
    return (
      <button
        type="button"
        onClick={refetch}
        title={title}
        aria-label={`Backend ${LABEL[status]}. Click to re-check.`}
        className={cn(
          'flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-muted transition-colors hover:bg-hover',
          className,
        )}
      >
        <span
          className={cn('h-2 w-2 rounded-full transition-colors', LED_CLASS[status])}
        />
      </button>
    )
  }

  return (
    <button
      type="button"
      onClick={refetch}
      title={title}
      aria-label={`Backend ${LABEL[status]}. Click to re-check.`}
      className={cn(
        'group inline-flex items-center gap-2 rounded-full border border-line bg-surface',
        'px-2.5 py-1.5 font-mono text-[11px] text-muted transition-colors hover:bg-hover',
        className,
      )}
    >
      <span
        className={cn(
          'h-1.5 w-1.5 shrink-0 rounded-full transition-colors',
          LED_CLASS[status],
        )}
      />
      <span className="hidden sm:inline">{LABEL[status]}</span>
      {status === 'online' && health && (
        <span className="hidden text-subtle lg:inline">v{health.version}</span>
      )}
      <RefreshIcon
        size={12.5}
        strokeWidth={2}
        className={cn(
          'text-subtle opacity-0 transition-opacity group-hover:opacity-100',
          isFetching && 'animate-spin opacity-100',
        )}
      />
    </button>
  )
}
